import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';
import FieldError from '../components/FieldError';
import { useFormErrors } from '../utils/useFormErrors';
import { formatCurrency, formatDate, today } from '../utils/helpers';

const empty = { vehicle_id:'', fuel_date:today(), odometer_reading:'', liters:0, rate:0, total_amount:0, fuel_station:'', remarks:'' };

export default function Fuel() {
  const [list, setList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filterStatus, setFilterStatus] = useState('active');

  // One rule per field marked * in the form below.
  const { errors, validate, clearError, reset: resetErrors, invalid } = useFormErrors({
    vehicle_id: { label: 'Vehicle' },
  });

  const setField = (key, value) => { setForm(f => ({ ...f, [key]: value })); clearError(key); };
  const openForm = () => { setForm(empty); resetErrors(); setOpen(true); };

  const load = () => api.get('/fuel').then(r => setList(r.data.data || []));
  useEffect(() => { load(); api.get('/vehicles').then(r => setVehicles((r.data.data || []).filter(v => v.status !== 'inactive'))); }, []);

  const updateCalc = (liters, rate) => {
    const total = parseFloat(liters||0) * parseFloat(rate||0);
    setForm(f => ({...f, liters, rate, total_amount: total.toFixed(2)}));
  };

  const save = async () => {
    if (!validate(form)) return;
    try { await api.post('/fuel', form); toast.success('Logged'); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const total = list.reduce((s, r) => s + parseFloat(r.total_amount || 0), 0);
  const totalLiters = list.reduce((s, r) => s + parseFloat(r.liters || 0), 0);
  const filtered = list.filter(f => {
    if (filterStatus === 'active') return Number(f.status) === 1;
    if (filterStatus === 'inactive') return Number(f.status) !== 1;
    return true;
  });

  const pendingCount = list.filter(x => x.approval_status === 'pending').length;

  return (
    <div className="card">
      <div className="card-header">
        <h3>Fuel Log ({filtered.length}) — Total: {formatCurrency(total)} · {totalLiters.toFixed(2)} Ltrs<PendingCountBadge count={pendingCount}/></h3>
        <button className="btn btn-blue" onClick={openForm}><Plus size={14}/> Log Fuel</button>
      </div>
      <div className="search-bar" style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{maxWidth:180}}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">Active + Inactive</option>
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Vehicle</th><th>Odometer</th><th className="td-num">Liters</th><th className="td-num">Rate</th><th className="td-num">Amount</th><th>Station</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length ? filtered.map(f => (
              <tr key={f.id} style={Number(f.status)!==1?{background:'#f8fafc',color:'#94a3b8'}:f.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                <td>{formatDate(f.fuel_date)}
                  {Number(f.status)!==1 && <span className="badge badge-gray" style={{marginLeft:6}}>Inactive</span>}
                </td>
                <td><strong>{f.vehicle_no}</strong>
                  {f.transporter_name && <small style={{display:'block',color:'#64748b',fontSize:11}}>{f.transporter_name}</small>}
                </td>
                <td>{f.odometer_reading || '-'}</td>
                <td className="td-num">{f.liters}</td>
                <td className="td-num">{formatCurrency(f.rate)}</td>
                <td className="td-num">{formatCurrency(f.total_amount)}</td>
                <td>{f.fuel_station || '-'}</td>
                <td><ApprovalBadge status={f.approval_status} createdByName={f.created_by_name}/></td><td><RowActions endpoint="/fuel" row={f} onChange={load} softDelete restoreAction="restore" confirmDeleteMsg="Delete this fuel log?"/></td>
              </tr>
            )) : <tr><td colSpan="9" className="empty">No fuel records</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="Log Fuel"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Vehicle *</label>
            <select value={form.vehicle_id} className={invalid('vehicle_id')} onChange={e=>setField('vehicle_id', e.target.value)}>
              <option value="">-- Select --</option>
              {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_no}{v.transporter_name?` · ${v.transporter_name}`:''}</option>)}
            </select>
            <FieldError msg={errors.vehicle_id}/>
          </div>
          <div className="form-group"><label>Date</label><input type="date" value={form.fuel_date} onChange={e=>setForm({...form, fuel_date:e.target.value})}/></div>
          <div className="form-group"><label>Odometer (KM)</label><input type="number" value={form.odometer_reading} onChange={e=>setForm({...form, odometer_reading:e.target.value})}/></div>
          <div className="form-group"><label>Liters</label><input type="number" step="0.01" value={form.liters} onChange={e=>updateCalc(e.target.value, form.rate)}/></div>
          <div className="form-group"><label>Rate per Liter</label><input type="number" step="0.01" value={form.rate} onChange={e=>updateCalc(form.liters, e.target.value)}/></div>
          <div className="form-group"><label>Total Amount</label><input type="number" value={form.total_amount} onChange={e=>setForm({...form, total_amount:e.target.value})}/></div>
          <div className="form-group full-width"><label>Fuel Station</label><input value={form.fuel_station} onChange={e=>setForm({...form, fuel_station:e.target.value})}/></div>
          <div className="form-group full-width"><label>Remarks</label><textarea value={form.remarks} onChange={e=>setForm({...form, remarks:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
